/**
 * A report's commercial history — who changed its price, dates, billing flags,
 * case name and CX lead, and a partner's fee and deadline, newest first.
 * Admin-only. `lastByField` / `lastForAssignment` give the most recent change
 * per field, for the "last changed by … on …" line beside a value.
 *
 * Refetch after an edit so the new row shows without a reload.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { lastChangeByField, type CommercialChange } from '@/types/commercial-change';

export interface UseCommercialChangesResult {
  changes: CommercialChange[];
  isLoading: boolean;
  error: Error | null;
  /** The report's own fields: field name → its most recent change. */
  lastByField: Record<string, CommercialChange>;
  /** One partner assignment's fields: field name → its most recent change. */
  lastForAssignment: (assignmentId: string) => Record<string, CommercialChange>;
  refetch: () => Promise<void>;
}

export function useCommercialChanges(reportId: string | null): UseCommercialChangesResult {
  const [changes, setChanges] = useState<CommercialChange[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // A response for a report we have since left must not overwrite the current one.
  const latestRequest = useRef(0);

  const refetch = useCallback(async () => {
    const request = ++latestRequest.current;
    if (!reportId) {
      setChanges([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await vnextApiClient.listCommercialChanges(reportId);
      if (request === latestRequest.current) {
        setChanges(result);
      }
    } catch (err) {
      if (request !== latestRequest.current) {
        return;
      }
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      setError(e);
    } finally {
      if (request === latestRequest.current) {
        setIsLoading(false);
      }
    }
  }, [reportId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const lastByField = useMemo(() => lastChangeByField(changes), [changes]);
  const lastForAssignment = useCallback(
    (assignmentId: string) => lastChangeByField(changes, assignmentId),
    [changes]
  );

  return { changes, isLoading, error, lastByField, lastForAssignment, refetch };
}
